// PR6 3b — the DS mobile recorder (ui_kits/mobile/Mobile.jsx's MRecorder): camera + mic only
// (no screen-share picker — a phone webview has nothing to pick), "Works offline" copy, and
// the "Waiting to upload (n)" list (pendingUploads.ts; the real multi-recording version is
// F5/#72).
//
// Purely presentational and driven by RecorderPage's own useRecorder()/UploadQueue state —
// recording and uploading stay single-sourced in RecorderPage so the mobile and desktop
// layouts can never diverge on FR-RD7/FR-P2's invariants; this file only swaps what gets drawn.
//
// F16 (brief §5, SD47): in the Tauri mobile shell a Camera / Screen choice appears. Screen is
// enabled only when the native bridge reports `screen.ready` (the F16a MediaProjection writer
// on Android, the F16b ReplayKit broadcast ingest on iOS). Until then it stays disabled, with
// the bridge's own per-OS reason shown underneath (FR-RD9: never look broken, never pretend).
import { useEffect, useRef } from "react";
import { Icon, IconButton } from "@grackle/ui";
import type { RecorderPhase } from "../../recorder/useRecorder";
import type { UploadQueueSnapshot } from "../../recorder/uploadQueue";
import { pendingUploadsFrom } from "../../recorder/pendingUploads";
import "./MobileRecorderScreen.css";

export type MobileCaptureChoice = "camera" | "screen";

/** The subset of the native `screen` bridge (desktop.ts NativeBridge) this screen needs. */
export interface MobileScreenBridge {
  available: boolean;
  ready: boolean;
  reason: string | null;
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function PreviewVideo({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.srcObject = stream;
    void video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [stream]);
  // Muted for the same reason as the desktop preview: the stream already carries the mic
  // track this tab is recording from, and playing it back out loud would echo into it.
  return <video ref={ref} muted playsInline />;
}

export default function MobileRecorderScreen({
  phase,
  countdown,
  title,
  previewStream,
  micLevels,
  elapsedMs,
  error,
  uploadSnapshot,
  onStart,
  onStop,
  onPauseToggle,
  onDiscard,
  screenBridge = null,
  captureChoice = "camera",
  onCaptureChoice,
}: {
  phase: RecorderPhase;
  countdown: number | null;
  title: string;
  previewStream: MediaStream | null;
  micLevels: number[];
  elapsedMs: number;
  error: string | null;
  uploadSnapshot: UploadQueueSnapshot | null;
  onStart: () => void;
  onStop: () => void;
  onPauseToggle: () => void;
  onDiscard: () => void;
  /** Present only in the mobile shell; `null` in a mobile browser (no native screen path). */
  screenBridge?: MobileScreenBridge | null;
  captureChoice?: MobileCaptureChoice;
  onCaptureChoice?: (choice: MobileCaptureChoice) => void;
}) {
  const capturing = phase === "recording" || phase === "paused";
  const showChoice = !!screenBridge?.available && !capturing;
  const screenReady = !!screenBridge?.ready;
  const pending = pendingUploadsFrom(uploadSnapshot, title);

  return (
    <div className="mrec" data-theme="dark">
      <div className="mrec__preview">
        {previewStream ? (
          <PreviewVideo stream={previewStream} />
        ) : (
          <div className="mrec__preview-placeholder">
            {phase === "priming" ? "Requesting camera and microphone…" : "Starting the camera…"}
          </div>
        )}
        {capturing ? (
          <span className="mrec__recpill">
            <span className="mrec__recpill-dot" />
            {phase === "paused" ? "PAUSED" : "REC"} <span className="mono">{formatElapsed(elapsedMs)}</span>
          </span>
        ) : null}
        {countdown !== null ? <div className="mrec__countdown">{countdown === 0 ? "" : countdown}</div> : null}
        {previewStream ? (
          <div className="mrec__meter" aria-hidden>
            {micLevels.map((level, i) => (
              <div key={i} className={`mrec__meter-bar${level > 0.04 ? " is-live" : ""}`} style={{ height: `${3 + level * 14}px` }} />
            ))}
          </div>
        ) : null}
      </div>

      {showChoice ? (
        <div className="mrec__mode">
          <div className="gk-seg gk-seg--block" role="group" aria-label="What to record">
            <button
              type="button"
              className="gk-seg__item"
              aria-pressed={captureChoice === "camera"}
              onClick={() => onCaptureChoice?.("camera")}
            >
              <Icon name="video-camera" size={16} />
              Camera
            </button>
            <button
              type="button"
              className="gk-seg__item"
              aria-pressed={captureChoice === "screen"}
              disabled={!screenReady}
              onClick={() => onCaptureChoice?.("screen")}
            >
              <Icon name="desktop" size={16} />
              Screen
            </button>
          </div>
          {!screenReady && screenBridge?.reason ? (
            <div className="mrec__mode-note" data-testid="screen-unavailable">
              {screenBridge.reason}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mrec__offline">
        <Icon name="cloud-slash" style={{ color: "var(--now)" }} />
        Works offline. The recording uploads in the background once you're back online.
      </div>

      {error ? <div className="mrec__error">{error}</div> : null}

      {pending.length > 0 ? (
        <div className="mrec__pending">
          <div className="mrec__pending-title">Waiting to upload ({pending.length})</div>
          {pending.map((p) => (
            <div className="mrec__pending-row" key={p.id}>
              <div className="mrec__pending-body">
                <span className="mrec__pending-name">{p.title}</span>
                <span className="mrec__pending-status">
                  {p.phase === "failed"
                    ? p.lastError ?? "Upload failed"
                    : p.phase === "finishing"
                      ? "Finishing…"
                      : `Uploading — ${Math.round((p.bytesAccepted / Math.max(1, p.bytesRecorded)) * 100)}%`}
                </span>
              </div>
              <div className="mrec__pending-actions">
                {/* TODO(F5): Retry stays hidden until F5 lands. A real retry needs the recording
                    to still exist on disk; nothing in this tab keeps the bytes after push(), so
                    today the only honest recovery is discarding and recording again. */}
                <button type="button" className="mrec__pending-btn" onClick={onDiscard}>
                  Discard
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mrec__controls">
        {capturing ? (
          <>
            <IconButton
              icon={phase === "paused" ? "play" : "pause"}
              label={phase === "paused" ? "Resume" : "Pause"}
              variant="glass"
              size="lg"
              onClick={onPauseToggle}
            />
            <button type="button" aria-label="Stop recording" className="mrec__stop" onClick={onStop}>
              <span className="mrec__stop-square" />
            </button>
            <div className="mrec__controls-spacer" />
          </>
        ) : (
          <button
            type="button"
            aria-label="Start recording"
            className="mrec__start"
            disabled={phase !== "ready" || countdown !== null}
            onClick={onStart}
          >
            {countdown !== null ? "Starting…" : "Start recording"}
          </button>
        )}
      </div>
    </div>
  );
}
